import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Text, Box, Grid, Divider
} from 'components/ui';
import * as rwdBasketConstants from 'constants/RwdBasket';
import CashBackRewards from 'components/RwdBasket/RwdBasketLayout/BIBenefits/CashBackRewards';
import CreditCardProgram from 'components/RwdBasket/RwdBasketLayout/BIBenefits/CreditCardProgram';
import RCPSCookies from 'utils/RCPSCookies';
import localeUtils from 'utils/LanguageLocale';
import BIBenefitsRewards from 'components/RwdBasket/RwdBasketLayout/BIBenefits/BIBenefitsRewards/BIBenefitsRewards';
import { BASKET_TYPES } from 'constants/RwdBasket';
import Empty from 'constants/empty';
import CCRewards from 'components/RwdBasket/RwdBasketLayout/BIBenefits/CCRewards/CCRewards';
import FeaturedOffers from 'components/RwdBasket/RwdBasketLayout/BIBenefits/FeaturedOffers';
import ErrorMessage from 'components/FrictionlessCheckout/ErrorMessage/ErrorMessage';
import ScrollAnchor from 'components/ScrollAnchor/ScrollAnchor';
import { SECTION_NAMES } from 'constants/frictionlessCheckout';
import agentAwareUtils from 'utils/AgentAware';

const {
    BI_BENEFITS_ITEM_TYPES: { BI_POINTS, ROUGE_REWARDS, CC_REWARDS },
    BASKET_RENDERING_TYPE_DYNAMIC_ATTRIBUTE: { ITEM_TYPE }
} = rwdBasketConstants;

const BIBenefits = props => {
    const {
        biBenefitsInfo,
        basketType,
        isGuestCheckout,
        sectionLevelError,
        localization,
        getText,
        rougeBadge,
        clearPromoError,
        isUserEligibleToRedeemBICash
    } = props;

    const renderCCRewardsTile = (ccTargeters, ccBanner, ccRewards) => {
        let compToRender = null;

        const { infoModalCallbacks } = biBenefitsInfo[basketType] || {};
        const creditCardRewardsModalData = infoModalCallbacks?.creditCardRewardsModal || {};

        if (localeUtils.isUS()) {
            if (ccRewards.showCCRewards) {
                compToRender = (
                    <CCRewards
                        {...ccRewards}
                        infoModalCallback={creditCardRewardsModalData}
                        clearPromoError={clearPromoError}
                    />
                );
            } else {
                if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
                    //do an early return if it is agent aware
                    return null;
                }

                if (RCPSCookies.isRCPSCCAP()) {
                    compToRender = ccBanner ? <CreditCardProgram ccTargeters={ccBanner} /> : null;
                } else {
                    compToRender = ccTargeters ? <CreditCardProgram ccTargeters={ccTargeters} /> : null;
                }
            }
        }

        return compToRender;
    };

    const updateRougeRewards = () => {
        const { rougeRewards } = biBenefitsInfo[basketType];

        const updatedRougeRewards = { ...rougeRewards, showChevron: rougeRewards.showRougeRewardsChevron && !rougeRewards.disableRougeRewards };

        return updatedRougeRewards;
    };

    const renderBIBenefitsTiles = basket => {
        const {
            biBenefitsContentData, cbr, infoModalCallbacks, ccBanner, ccTargeters, ccRewards
        } = basket;
        const rougeRewards = basketType === BASKET_TYPES.DC_BASKET ? updateRougeRewards() : Empty.Object;

        const biBenefitsTiles = biBenefitsContentData
            ?.map(comp => {
                const itemType = comp.items?.find(item => item.key.toLowerCase() === ITEM_TYPE.toLowerCase())?.value || '';

                const tile =
                    itemType.length > 0 &&
                    {
                        [BI_POINTS]: cbr.isAvailable ? (
                            <CashBackRewards
                                {...cbr}
                                infoModalCallback={infoModalCallbacks.cbr}
                            />
                        ) : null,
                        [CC_REWARDS]: renderCCRewardsTile(ccTargeters, ccBanner, ccRewards),
                        [ROUGE_REWARDS]: rougeRewards?.showRougeRewardsUI ? (
                            <BIBenefitsRewards
                                {...rougeRewards}
                                onBasketSwitch={() => {}}
                                rougeInfoModalCallback={infoModalCallbacks.applyRougeRewards}
                                rougeBadgeText={localization.rougeBadge}
                                isCheckout
                            />
                        ) : null
                    }[itemType];

                return tile ? { key: itemType, component: tile } : null;
            })
            .filter(value => value != null);

        if (!biBenefitsTiles?.length) {
            return null;
        }

        return (
            <Grid
                borderRadius={2}
                width='100%'
                display='block'
                lineHeight='tight'
                marginTop={4}
                marginBottom={2}
                backgroundColor={'white'}
                gap={3}
                alignItems={'center'}
            >
                {biBenefitsTiles &&
                    biBenefitsTiles.map((tile, i) => (
                        <React.Fragment key={tile.key}>
                            {tile.component}
                            {i < biBenefitsTiles.length - 1 && <Divider />}
                        </React.Fragment>
                    ))}
            </Grid>
        );
    };

    const renderTitle = basket => {
        return (
            <>
                <Text
                    is='h2'
                    fontSize={['md', 'md', 'lg']}
                    fontWeight='bold'
                    lineHeight={['tight', 'tight', 'none']}
                >
                    {localization.title}
                </Text>
                {(!sectionLevelError || sectionLevelError?.length === 0) && (
                    <Text
                        is='p'
                        marginTop={1}
                        marginBottom={4}
                        dangerouslySetInnerHTML={{
                            __html: getText('biPoints', [basket?.biAccount?.biPoints])
                        }}
                    />
                )}
            </>
        );
    };

    const renderPromoCodeSection = (basket, hasBIBenefitsTiles) => {
        const { infoModalCallbacks, bopisFeaturedOffers } = basket;

        const updatedInfoModalCallbacks = {
            ...infoModalCallbacks,
            featuredOffers: infoModalCallbacks?.biFeaturedOffers
        };

        return (
            <Box
                backgroundColor='white'
                marginTop={!hasBIBenefitsTiles ? 4 : 0}
            >
                <FeaturedOffers
                    infoModalCallbacks={updatedInfoModalCallbacks}
                    bopisFeaturedOffers={bopisFeaturedOffers}
                    featuredOffersMissing={!updatedInfoModalCallbacks?.featuredOffers}
                    isFrictionlessCheckout
                    shouldAddMarginTop={isUserEligibleToRedeemBICash}
                />
            </Box>
        );
    };

    const renderErrorMessage = errorMessage => {
        return <ErrorMessage message={errorMessage} />;
    };

    const renderScrollAnchor = () => {
        return (
            <ScrollAnchor
                id={SECTION_NAMES.BI_BENEFITS}
                hasOffset={false}
            />
        );
    };

    const basket = biBenefitsInfo[basketType] || {};
    const biBenefitsTiles = renderBIBenefitsTiles(basket);

    return (
        <Box
            borderRadius={2}
            display='block'
            paddingX={[4, 4, 3]}
            paddingTop={!isGuestCheckout && 4}
            paddingBottom={4}
            lineHeight='tight'
            backgroundColor='nearWhite'
            marginX={['-container', 0]}
            aria-label={rougeBadge}
            role='region'
        >
            {renderScrollAnchor()}

            {!isGuestCheckout && isUserEligibleToRedeemBICash && renderTitle(basket)}

            {sectionLevelError?.length > 0 && <Box marginTop={1}>{renderErrorMessage(sectionLevelError)}</Box>}

            {biBenefitsTiles}

            {renderPromoCodeSection(basket, Boolean(biBenefitsTiles))}
        </Box>
    );
};

export default wrapFunctionalComponent(BIBenefits, 'BIBenefits');
